// src/components/pages/Auth/AccountPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

import AccountTabs from "./AccountTabs";

import {
  fonts,
  getCurrentLangCode,
  gqlFetchMyProfile,
  normalizeWpUser,
  tForLang,
} from "./AccountUtils";

import {
  wpGetCachedUser,
  wpFetchMe,
  wpHasAuthToken,
  wpOnAuthChanged,
  wpSetCachedUser,
} from "../../../api/wpAuth";

/* -------------------------- URL tab helper -------------------------- */
const useTab = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get("tab") || "dashboard";
};

export default function AccountPage() {
  const tab = useTab();
  const location = useLocation();

  const [lang, setLang] = useState(getCurrentLangCode());
  const [viewer, setViewer] = useState(() => normalizeWpUser(wpGetCachedUser()));
  const user = viewer;

  const t = (key) => tForLang(lang, key);

  useEffect(() => {
    const handler = () => setLang(getCurrentLangCode());
    window.addEventListener("app:languageChanged", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("app:languageChanged", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  useEffect(() => {
    const unsub = wpOnAuthChanged((detail) => {
      if (detail && "user" in detail) {
        setViewer(normalizeWpUser(detail.user));
      } else {
        setViewer(normalizeWpUser(wpGetCachedUser()));
      }
    });
    return () => unsub?.();
  }, []);

  // Fetch full profile (birthday + gender) reliably
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!wpHasAuthToken()) return;

        try {
          const data = await gqlFetchMyProfile();
          if (!alive) return;
          const v = normalizeWpUser(data?.viewer);
          if (v) setViewer(v);
          wpSetCachedUser(v);
          return;
        } catch (_) {
          // fallback
        }

        const me = await wpFetchMe({ debug: false });
        if (!alive) return;
        const v2 = normalizeWpUser(me);
        setViewer(v2);
        wpSetCachedUser(v2);
      } catch (e) {
        if (!alive) return;
        setViewer(normalizeWpUser(wpGetCachedUser()));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const onUserUpdated = (u) => setViewer(normalizeWpUser(u));

  const pageTitle = useMemo(() => t("pageTitle"), [lang, location.search]); // tiny re-render safety

  return (
    <Box sx={{ py: { xs: 2, md: 5 } }}>
      <Container maxWidth="lg">
        <Typography
          variant="h5"
          sx={{
            fontFamily: fonts[lang],
            fontWeight: 900,
            mb: 1.5,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          {pageTitle}
        </Typography>

        <AccountTabs tab={tab} lang={lang} user={user} onUserUpdated={onUserUpdated} />
      </Container>
    </Box>
  );
}
